/**
 * Type parameter editor row for the Edit Type table. Renders the correct
 * control per editor kind; length values are stored in meters and displayed
 * in millimeters. Values are committed through the parent draft state.
 */

import { Input, Select, HStack, Box } from '@chakra-ui/react';
import { TypeParameterDef } from '../../config/typeParameterSchemas';
import { useMaterialNameOptions } from './materialCatalog';

interface TypeParameterRowProps {
    def: TypeParameterDef;
    value: any;
    readOnly: boolean;
    onChange: (next: any) => void;
}

/** Format a stored value for display. */
function displayValue(def: TypeParameterDef, value: any): string {
    if (value === undefined || value === null) return '';
    if (def.editor === 'length' && typeof value === 'number') {
        return String(Math.round(value * 1000 * 1000) / 1000);
    }
    if (def.editor === 'number' && typeof value === 'number') {
        return String(Math.round(value * 1000) / 1000);
    }
    if (def.editor === 'boolean') return value === true || value === 'Yes' ? 'Yes' : 'No';
    return String(value);
}

export function TypeParameterRow({ def, value, readOnly, onChange }: TypeParameterRowProps) {
    const label = def.unit ? `${def.label} (${def.unit})` : def.label;
    const materialOptions = useMaterialNameOptions();

    const inputStyle = {
        bg: 'var(--color-bg-tertiary)',
        borderColor: 'var(--color-border)',
        _hover: { borderColor: 'var(--color-accent)' },
        _focus: { borderColor: 'var(--color-accent)' },
    } as const;

    if (readOnly) {
        return (
            <Input
                size="sm"
                isReadOnly
                title={def.helpText}
                value={displayValue(def, value)}
                {...inputStyle}
                color="var(--color-text-secondary)"
                cursor="default"
            />
        );
    }

    switch (def.editor) {
        case 'boolean':
        case 'select':
        case 'material':
        case 'pattern': {
            const options =
                def.editor === 'material' ? materialOptions
                    : def.editor === 'boolean' ? ['Yes', 'No']
                        : (def.options ?? []);
            const current = displayValue(def, value);
            // Preserve unknown legacy values as a selectable option.
            const withCurrent = options.includes(current) || current === '' ? options : [...options, current];
            return (
                <Select
                    size="sm"
                    title={def.helpText}
                    value={current}
                    onChange={(e) => {
                        const raw = e.target.value;
                        if (def.editor === 'boolean') onChange(raw === 'Yes');
                        else onChange(raw);
                    }}
                    {...inputStyle}
                >
                    <option value="">—</option>
                    {withCurrent.map((opt: string) => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </Select>
            );
        }

        case 'color':
            return (
                <HStack spacing={2}>
                    <Box
                        as="input"
                        type="color"
                        value={typeof value === 'string' && value.startsWith('#') ? value : '#000000'}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                        w="32px"
                        h="28px"
                        p={0}
                        flexShrink={0}
                        cursor="pointer"
                        bg="transparent"
                        borderColor="var(--color-border)"
                        borderWidth="1px"
                        borderRadius="var(--cad-radius-sm)"
                    />
                    <Input
                        size="sm"
                        title={def.helpText}
                        value={String(value ?? '')}
                        onChange={(e) => onChange(e.target.value)}
                        {...inputStyle}
                    />
                </HStack>
            );

        case 'length':
        case 'number': {
            const numeric = typeof value === 'number' ? value : parseFloat(String(value));
            const shown = def.editor === 'length' && !Number.isNaN(numeric)
                ? String(Math.round(numeric * 1000 * 1000) / 1000)
                : Number.isNaN(numeric) ? '' : String(numeric);
            return (
                <Input
                    size="sm"
                    type="number"
                    step={def.editor === 'length' ? 1 : 0.01}
                    title={def.helpText}
                    value={shown}
                    onChange={(e) => {
                        const parsed = parseFloat(e.target.value);
                        if (Number.isNaN(parsed)) return onChange(0);
                        onChange(def.editor === 'length' ? parsed / 1000 : parsed);
                    }}
                    {...inputStyle}
                />
            );
        }

        default:
            return (
                <Input
                    size="sm"
                    title={def.helpText}
                    value={String(value ?? '')}
                    onChange={(e) => onChange(e.target.value)}
                    {...inputStyle}
                />
            );
    }
}
